import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { account, ApiError, auth, errorMessage, onUnauthorized } from '../api/client';
import type { Profile, ProfilePatch, User } from '../api/types';

/**
 * Auth-aware application store. The current user comes from the API on load; nothing about
 * progress or sessions is kept in the browser. All scenario logic runs on the server.
 */
export interface Store {
  /** 'loading' until GET /auth/me has answered once. */
  status: 'loading' | 'ready';
  user?: User;
  profile?: Profile;
  /** A non-auth problem on load (e.g. server unreachable). */
  error?: string;
  login(email: string, password: string): Promise<void>;
  register(email: string, password: string, displayName: string): Promise<void>;
  logout(): Promise<void>;
  refreshProfile(): Promise<Profile | undefined>;
  /** PATCH the profile; the store keeps the returned profile. Errors propagate to the caller. */
  updateProfile(patch: ProfilePatch): Promise<Profile | undefined>;
  /** Apply a profile returned by another call without a second request. */
  setProfile(profile: Profile | undefined): void;
  setUser(user: User | undefined): void;
}

const StoreContext = createContext<Store | undefined>(undefined);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');
  const [user, setUser] = useState<User>();
  const [profile, setProfile] = useState<Profile>();
  const [error, setError] = useState<string>();

  const loadProfile = useCallback(async (): Promise<Profile | undefined> => {
    try {
      const { profile: p } = await account.profile();
      setProfile(p);
      return p;
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 401)) setError(errorMessage(e));
      return undefined;
    }
  }, []);

  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const { user: u } = await auth.me();
        if (canceled) return;
        setUser(u);
        await loadProfile();
      } catch (e) {
        if (canceled) return;
        if (!(e instanceof ApiError && e.status === 401)) setError(errorMessage(e));
      } finally {
        if (!canceled) setStatus('ready');
      }
    })();
    return () => {
      canceled = true;
    };
  }, [loadProfile]);

  // A 401 anywhere means the server session ended (expiry, password change, deletion): drop the login.
  useEffect(
    () =>
      onUnauthorized(() => {
        setUser(undefined);
        setProfile(undefined);
      }),
    [],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const { user: u } = await auth.login(email, password);
      setUser(u);
      setError(undefined);
      await loadProfile();
    },
    [loadProfile],
  );

  const register = useCallback(
    async (email: string, password: string, displayName: string) => {
      const { user: u } = await auth.register(email, password, displayName);
      setUser(u);
      setError(undefined);
      await loadProfile();
    },
    [loadProfile],
  );

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      setUser(undefined);
      setProfile(undefined);
    }
  }, []);

  const updateProfile = useCallback(async (patch: ProfilePatch) => {
    const { profile: p } = await account.update(patch);
    setProfile(p);
    setUser((u) => (u ? { ...u, displayName: p.displayName } : u));
    return p;
  }, []);

  const value = useMemo<Store>(
    () => ({ status, user, profile, error, login, register, logout, refreshProfile: loadProfile, updateProfile, setProfile, setUser }),
    [status, user, profile, error, login, register, logout, loadProfile, updateProfile],
  );
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): Store {
  const s = useContext(StoreContext);
  if (!s) throw new Error('useStore outside StoreProvider');
  return s;
}
